import * as React from 'react';
import { connect } from 'react-redux';
import { ConfigurationModel, TroughModel } from 'franke-trough-dependencies';
import { GlobalPrivateState, ActiveFilterValue, setFilters, listOfActiveFilters } from 'src/api';
import { RequestApiResult } from 'loopback-react-request';
import Button from 'reactstrap/lib/Button';
import Collapse from 'reactstrap/lib/Collapse';
import TroughListItem from './TroughListItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import './TroughListItem.scss';
import FilterForm from '../TroughFilter/TroughFilter';
import SelectedTroughList from '../SelectedTroughList/SelectedTroughList';

import './TroughList.scss';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './TroughList.messages';
import { WarningsTooltip } from '../../Configuration';
import { isConfigurationEditable, isConfigurationEditableByCurrentUser } from 'src/redux/utils.redux';

export interface TroughListProps {
  configuration: GlobalPrivateState['configuration'];
  filter: GlobalPrivateState['queryState']['params']['filter'];
  params: GlobalPrivateState['queryState']['params'];
  troughs: GlobalPrivateState['troughs'];
  warning: GlobalPrivateState['warning'];
}

export interface TroughListState {
  collapse: boolean;
}

export class TroughListPartial extends React.Component<TroughListProps, TroughListState> {
  
  constructor(props: TroughListProps) {
    super(props);
    this.toggle = this.toggle.bind(this);
    this.state = { 
      collapse: false,
    };    
  }

  componentDidUpdate(prevProps: TroughListProps) {
    const { configuration } = this.props;
    const troughId = this.getTroughId(configuration);
    const prevTroughId = this.getTroughId(prevProps.configuration);
    if (troughId && troughId !== prevTroughId) {
      this.toggle();
    }
  }

  public render() {
    const { troughs, warning } = this.props;
    return (
      <div className="partial-trough-list-options">
        {isConfigurationEditable() && (
          <WarningsTooltip itemKey="trough" />
        )}
        <div className="trough-list-partial">
          <div className="select-trough-btn border-bottom pb-4">
            <Button 
              onClick={this.toggle} 
              style={{ marginBottom: '1rem' }} 
              disabled={!isConfigurationEditable() || warning.requireReset.trough.required}
            >
              {!this.state.collapse ? (
                <>
                  <FormattedMessage {...MESSAGES.troughListButtonSelect} />
                </>
              ) : (
                <>
                  <FormattedMessage {...MESSAGES.troughListButtonClose} />
                </>
              )}
            </Button>
          </div>
          {isConfigurationEditableByCurrentUser() && (
            <div className="select-trough-body">
              <Collapse isOpen={this.state.collapse}>
                <div className="trough trough-selection">
                  <div className="row h-100">
                  {troughs && troughs.result && troughs.result.length > 0 && (
                    <div className={`trough-selection-wrap d-flex align-content-start flex-wrap ${this.activeFilter ? 'active-select' : ''}`}>
                      {troughs.result.map((trough, index) => {
                        if (trough.deleted) { return; } // Hide in front-end, because the trough could still be used for an existing config which we dont want to break

                        const key = `troughs_list_item_${index}_${trough.id}`;
                        return( 
                          <TroughListItem 
                            activeFilters={this.activeFilter}
                            key={key} 
                            trough={trough} 
                            disable={this.disableTrough(trough)} 
                            troughId={trough.id}
                          />
                        );
                      })}
                    </div>
                  )}
                  {troughs && troughs.result && troughs.result.length === 0 && (
                    <div className={`trough-selection-wrap align-content-end flex-wrap`}>
                      <div>
                        <h3> 
                          <FormattedMessage {...MESSAGES.troughListSorryMsg} /></h3>  
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </Collapse>
            </div>
          )}
        </div>
        <div className="trough-filter-options-partial">
          {isConfigurationEditableByCurrentUser() && (
            <div className="trough-filter-options-collapse">
              <div className="filter-options-button">
                <Button 
                  onClick={this.toggle} 
                  style={{ marginBottom: '1rem' }} 
                  className={`w-100 border-0 text-left p-0 pt-4 pb-4 ${this.state.collapse ? 'show' : ''}`}
                  disabled={this.props.warning.requireReset.trough.required ? true : false}
                >
                  <FormattedMessage {...MESSAGES.troughListButtonFilterOptions} />
                  <strong><FontAwesomeIcon icon={faChevronDown} /></strong>
                </Button>
              </div>
              <div className="filter-options-form">
                <Collapse isOpen={this.state.collapse}>
                  <FilterForm />
                </Collapse>
              </div>
              <span className="border-span" />
            </div>
          )}
          <div className="trough-list-selected-item">
            <SelectedTroughList />
          </div>
        </div>
      </div>
    );
  }

  private get activeFilter(): ActiveFilterValue[] | undefined {
    const { filter } = this.props;
    const activeList = filter && setFilters(filter);
    
    if (!activeList) {
      return;
    }

    return listOfActiveFilters(activeList);
  }

  private getTroughId(configuration: RequestApiResult<ConfigurationModel>): string | undefined {
    return configuration && configuration.result && configuration.result.troughId;
  }

  private toggle() {
    this.setState({ 
      collapse: !this.state.collapse,
    });
  }

  private disableTrough(trough: TroughModel): boolean {
    const { params } = this.props;
    
    if (!isConfigurationEditable()) {
      return true;
    }

    let touched = false;
    if (params && params.filter) {
      for (const key in params.filter) {
        if (params.filter[key]) {
          touched = true;
          if (trough[key]) { return false; }
        }
      }
    }
    return touched; // some filters are applied but no active filter is set true in given trough
  }
}

// https://daveceddia.com/access-redux-store-outside-react/

export const TroughList = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
  filter: state.queryState.params.filter,
  params: state.queryState.params,
  troughs: state.troughs,
  warning: state.warning,
}))(TroughListPartial);

export default TroughList;

